import { FC } from "react";
import { twMerge } from "tailwind-merge";

import { sendAISummarizeResponsesQuery } from "components/ai/queries/chat-query";
import { SECONDARY_OVERLAY_QUERY_PARAM, SecondaryOverlay } from "components/constants";
import { HoverableEditField } from "components/hover-widgets/EditableField";
import { HoverableToolbar } from "components/hover-widgets/HoverableToolbar";
import { PageNavigationButtons } from "components/materials/ContentNavigationButtons";
import { useIsAssessing } from "components/materials/section/assessment/hooks";
import { PageSidebarOptionsMenu } from "components/menus/PageSidebarOptionsMenu";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { useCapabilities } from "store/capability-hooks";
import { useAppDispatch, useAppSelector } from "store/index";
import { selectIsEditModeEnabled } from "store/selectors";
import { aiStateActions } from "store/slices/ai";
import { usePageActions } from "utils/page";
import { useQueryParamsManager } from "utils/routing";

type PageTitlebarProps = {
  page: PageType | null;
  selectedTopicId?: string;
  pages: PageType[];
};

export const PageTitlebar: FC<PageTitlebarProps> = ({ page, selectedTopicId, pages }) => {
  const isMobile = !useIsMdOrLarger();
  const { canAuthorSection, canUseBetaFeatures } = useCapabilities();
  const isAssessing = useIsAssessing();
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const dispatch = useAppDispatch();
  const { setQueryParams } = useQueryParamsManager();
  const canSummarizeResponses = canAuthorSection && !isAssessing;

  const { savePage, navigateToPage } = usePageActions();

  const onPageTitleChanged = (newTitle: string) =>
    page && savePage({ id: page.id, title: newTitle });
  const onPageChange = (selectedPageId: string, pageUrl: URL) => {
    const selectedPage = pages.find((p) => p.id === selectedPageId) || null;
    // TODO: Don't even pass the URL object, just the pathname + search, in the first place
    navigateToPage(selectedPage, pageUrl.pathname + pageUrl.search);
  };

  const onSummarizeResponses = () => {
    if (!page) {
      return;
    }
    // Ensure AI tab is open
    setQueryParams({ [SECONDARY_OVERLAY_QUERY_PARAM]: SecondaryOverlay.AIAssistant });

    // Ensure student response are enabled first
    dispatch(aiStateActions.setStudentResponsesForAIEnabled(true));
    dispatch(aiStateActions.clearAiChatMessages());

    // Note that this query acts on the _latest_ version of all the student responses, not their submissions,
    // which we might want to call out or allow changing.
    // Task wording lives in server ContextBuilder (summarize_responses_mode); prompt is worksheet + permissions only.
    sendAISummarizeResponsesQuery(dispatch);
  };

  if (!page) {
    return null;
  }
  return (
    <div className="flex w-full shrink-0 grow-0 flex-col items-center px-4 md:px-10">
      <div
        className={twMerge(
          "mb-4 flex w-full max-w-[--page-default-max-width] items-start justify-between",
          isMobile ? "mt-5" : "mt-8",
        )}
      >
        <div className="-my-1 flex-auto pr-2">
          <HoverableToolbar
            className="relative"
            contentClassName="mt-1 bg-white"
            readOnly={!canAuthorSection}
            reference="page"
            disableUnlessEditMode
            showEditButton
            uncentered
          >
            <h2>
              <HoverableEditField
                label={t("glossary.page_title")}
                textSize={isMobile ? "h3" : "h2"}
                value={page.title}
                disableUnlessEditMode
                shouldWrap
                onValueChanged={onPageTitleChanged}
              />
            </h2>
          </HoverableToolbar>
        </div>

        <div
          className={twMerge(
            "navigation-buttons flex items-center gap-2",
            isAssessing ? "" : "mr-4", // fixes an issue where PageTitlebar's right padding disappears on smaller screens
          )}
        >
          {canAuthorSection && (
            <PageSidebarOptionsMenu
              currentTopicId={selectedTopicId}
              direction="bottom"
              editMode={isEditModeEnabled}
              kind="secondary"
              page={page}
              showDownloadUploadOptions={canUseBetaFeatures}
              showTimeline={canAuthorSection}
              onSummarizeResponses={canSummarizeResponses ? onSummarizeResponses : undefined}
            />
          )}

          {!isAssessing && <PageNavigationButtons pages={pages} onChange={onPageChange} />}
        </div>
      </div>
    </div>
  );
};
